import { useState, useMemo, useCallback, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Typography,
  Button,
  Link,
  Box,
  Card,
  CardContent,
  LinearProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ReactComponent as AttachFileIcon } from "@assets/images/icon/ico_download.svg";
import { useSearchParams, useParams } from "react-router-dom";
import { useIsMobile } from "@/hooks/Responsive";
import BasicModal from "@publish/components/Modal/BasicModal";
import AlertConfirmModal from "@components/Modal/AlertConfirmModal";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SurveyImg from "@assets/images/sub/img_survey.svg";
import { fetchMyCourseLectureList } from "@/api/mypage";
import { useAuth } from "@/components/AuthContext";
import { getVLabLectureAttachFile } from "@/api/vx";
import { downloadFile } from "@/api/util";

function formatContentTm(tm) {
  let seconds = Number(tm);
  if (seconds < 60) {
    return "00:" + seconds;
  }
  // sec
  var mins = Math.floor(seconds / 60);
  var secs = seconds - mins * 60;
  return addZero(mins) + ":" + addZero(secs);

  function addZero(num) {
    return (num < 10 ? "0" : "") + num;
  }
}

export default function LectureListTab() {
  const { user } = useAuth();

  const { isMobile } = useIsMobile();
  const [openModal, setOpenModal] = useState(false);
  const [selected, setSelected] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const [downloadConfirmOpen, setDownloadConfirmOpen] = useState(false);
  const [downloadTargetId, setDownloadTargetId] = useState(null);
  const [attachFile, setAttachFile] = useState(null);

  function LectureAttachment() {
    if (!attachFile) return null;

    const ext = attachFile.lecturePlanFileName?.split(".").pop();

    const fileName = `강좌계획표.${ext}`;

    return (
        <Stack direction="row" justifyContent="flex-end">
          <Link
              component="button"
              underline="hover"
              onClick={() => downloadFile(attachFile.lecturePlanFileUrl, fileName)}
              sx={{
                mt: 0,
                px: 2,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
              }}
          >
            <AttachFileIcon />

            <Typography component="span" color="text.primary">
              {fileName}
            </Typography>
          </Link>
        </Stack>
    );
  }

  // HMD일때 다운로드 컨펌창 xr과 다르게 바로 다운로드 돼서 소스 수정함
  const handleDownloadConfirm = () => {
    const target = grouped.flatMap(s => s.lectures).find(l => l.eduCourseId === downloadTargetId);

    if (!target?.contentUrl) return;

    setDownloadConfirmOpen(false);

    // confirm 이후 네이티브 다운로드
    window.location.href = target.contentUrl;
  };

  const handleModalOpen = row => {
    if (row?.contentType?.includes("HMD")) {
      setSelected({ courseName: row.courseName });
      setDownloadTargetId(row.eduCourseId);
      setDownloadConfirmOpen(true);
      return;
    }

    setSelected(row);
    setOpenModal(true);
  };

  const handleModalClose = useCallback(() => {
    setOpenModal(false);
    setSelected(null);
  }, []);

  const handleSurveyClick = useCallback(() => {
    searchParams.set("tab", "survey");
    setSearchParams(searchParams);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  }, [searchParams, setSearchParams]);

  const { id } = useParams();
  const contentSrc = useMemo(() => {
    if (!selected?.contentUrl) return undefined;
    try {
      const url = new URL(selected.contentUrl, window.location.origin);
      if (user?.accountId) url.searchParams.set("accountId", user.accountId);
      if (user?.accountIdSeq != null && user.accountIdSeq !== "") {
        url.searchParams.set("accountIdSeq", String(user.accountIdSeq));
      }
      const lectureId = selected.eduLectureId || id;
      if (lectureId) url.searchParams.set("lectureId", lectureId);
      return url.toString();
    } catch {
      return selected.contentUrl;
    }
  }, [selected, user, id]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getLectureAttachFile = async () => {
      setLoading(true);
      try {
        const response = await getVLabLectureAttachFile({ params: { eduLectureId: id } });
        // The API doesn't exist, so I'll use the hardcoded data as a mock response.
        setAttachFile(response.data);
      } catch (error) {
        console.error("Failed to fetch lecture File:", error);
      } finally {
        setLoading(false);
      }
    };
    const getLectureList = async () => {
      setLoading(true);
      try {
        const response = await fetchMyCourseLectureList({ params: { accountId: user.accountId, eduLectureId: id } });
        setAllItems(response.data || []);
      } catch (error) {
        console.error("Failed to fetch lecture list:", error);
      } finally {
        setLoading(false);
      }
    };

    getLectureList();
    getLectureAttachFile();
  }, [id]);

  const grouped = useMemo(() => {
    const result = [];
    let currentSection = null;

    allItems.forEach(item => {
      if (item.sortSeq === "0") {
        currentSection = { ...item, lectures: [] };
        result.push(currentSection);
      } else if (item.type === "survey") {
        if (result.length) result[result.length - 1].survey = item;
      } else if (currentSection) {
        currentSection.lectures.push(item);
      }
    });
    return result;
  }, [allItems]);
  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  /**
   * 각 항목 별 data
   */

  return (
      <>
        <LectureAttachment isMobile={isMobile} />

        {grouped.map((section, idx) => (
            <Box key={section.eduSectionId}>
              <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 4,
                    overflow: "hidden",
                    mt: 1.5,
                  }}
              >
                <Accordion defaultExpanded={idx === 0} sx={{ boxShadow: "none", "&:before": { display: "none" } }}>
                  <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        minHeight: "auto",
                        p: 3,
                        "& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded": {
                          m: 0,
                        },
                      }}
                  >
                    <Stack direction="row" alignItems="center">
                      <Typography fontWeight={500}>{String(idx + 1).padStart(2, "0")}</Typography>
                      <Box
                          sx={{
                            width: "1px",
                            height: "16px",
                            bgcolor: "divider",
                            mx: 1,
                          }}
                      />
                      <Typography>{section.eduSectionName}</Typography>
                    </Stack>
                  </AccordionSummary>

                  <AccordionDetails sx={{ p: 0, px: isMobile ? 2 : 3 }}>
                    {section.lectures.map((lec, idx) => {
                      const progress = Number(lec?.courseProgressRate ?? 0);
                      return (
                          <Stack
                              key={lec.eduCourseId}
                              direction={isMobile ? "column" : "row"}
                              justifyContent="space-between"
                              alignItems={isMobile ? "flex-start" : "center"}
                              sx={{
                                borderTop: "1px solid",
                                borderColor: "divider",
                                p: 0,
                                py: 3,
                              }}
                          >
                            <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                  color={lec.contentsCategory === "체험형" ? "secondary2.main" : "secondary.main"}
                                  fontWeight={600}
                              >
                                {lec.lectureCategory}
                              </Typography>
                              <Typography variant="subtitle3" component="p" fontWeight={600}>
                                {lec.courseName ?? "제목 없음"}
                              </Typography>
                              <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
                                <Typography variant="body2" component="span" color="text.secondary">
                                  {lec.lecturerName ?? "강사"}
                                </Typography>

                                <Box
                                    sx={{
                                      width: "1px",
                                      height: "12px",
                                      bgcolor: "divider",
                                      display: isMobile ? "none" : "block",
                                    }}
                                />

                                <Typography variant="body2" component="span" color="text.secondary">
                                  {lec.eduMethodTypeName ?? "PC 기반 실행"}
                                </Typography>

                                <Box
                                    sx={{
                                      width: "1px",
                                      height: "12px",
                                      bgcolor: "divider",
                                      display: isMobile ? "none" : "block",
                                    }}
                                />

                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                  <AccessTimeIcon sx={{ color: "text.secondary", fontSize: 16 }} />
                                  <Typography variant="body2" component="span" color="text.secondary">
                                    {lec?.contentTm ? formatContentTm(lec?.contentTm) : "00:00"}
                                  </Typography>
                                </Stack>

                                <Box
                                    sx={{
                                      width: "1px",
                                      height: "12px",
                                      bgcolor: "divider",
                                      display: isMobile ? "none" : "block",
                                    }}
                                />
                                <Stack direction="row" alignItems="center" spacing={0} sx={{ minWidth: 0 }}>
                                  <Typography
                                      variant="body2"
                                      component="span"
                                      color="text.secondary"
                                      sx={{ whiteSpace: "nowrap", flexShrink: 0, mr: isMobile ? 1 : 1.4 }}
                                  >
                                    진도율
                                  </Typography>
                                  <Box sx={{ width: isMobile ? "118px" : "120px" }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={progress}
                                        sx={{
                                          height: "6px",
                                          borderRadius: 1,
                                          bgcolor: "divider",
                                          "& .MuiLinearProgress-bar": {
                                            bgcolor: "primary.main",
                                          },
                                        }}
                                    />
                                  </Box>
                                  <Typography
                                      variant="body2"
                                      component="span"
                                      color="text.secondary"
                                      sx={{
                                        minWidth: isMobile ? "32px" : "36px",
                                        textAlign: "left",
                                        flexShrink: 0,
                                        whiteSpace: "nowrap",
                                        ml: isMobile ? 0.9 : 1.2,
                                      }}
                                  >
                                    {progress}%
                                  </Typography>
                                </Stack>
                              </Stack>
                            </Stack>

                            <Button
                                variant="contained"
                                color="primary"
                                size={isMobile ? "medium" : "large"}
                                sx={{ width: isMobile ? "100%" : "auto", mt: isMobile ? 3 : 0 }}
                                onClick={() => handleModalOpen(lec)}
                            >
                              강의보기
                            </Button>
                          </Stack>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              </Box>

              <Box>
                {section.survey && (
                    <Card variant="grey" sx={{ mt: 2 }}>
                      <CardContent
                          sx={{
                            display: "flex",
                            flexDirection: isMobile ? "column" : "row",
                            justifyContent: "space-between",
                            alignItems: isMobile ? "flex-start" : "center",
                            p: 3,
                            gap: isMobile ? 0 : 2,
                          }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center">
                          {!isMobile && (
                              <Box
                                  component="img"
                                  src={SurveyImg}
                                  alt="설문"
                                  sx={{
                                    width: "102px",
                                    height: "64px",
                                    objectFit: "cover",
                                  }}
                              />
                          )}
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {section.survey.title}
                            </Typography>
                            <Typography>기간 {section.survey.period}</Typography>
                          </Box>
                        </Stack>

                        <Button
                            variant="outlined"
                            color="primary"
                            size="medium"
                            sx={{ width: isMobile ? "100%" : "auto", mt: isMobile ? 3 : 0 }}
                            onClick={handleSurveyClick}
                        >
                          등록
                        </Button>
                      </CardContent>
                    </Card>
                )}
              </Box>
            </Box>
        ))}

        {/* HMD인경우 */}
        <AlertConfirmModal
            open={downloadConfirmOpen}
            title={`${selected?.courseName} 다운로드`}
            description="해당 콘텐츠 파일을 다운로드 하시겠습니까?"
            type="confirm"
            onConfirm={handleDownloadConfirm}
            onClose={() => setDownloadConfirmOpen(false)}
            onCancel={() => setDownloadConfirmOpen(false)}
        />

        {/* 강의보기 모달 */}
        <BasicModal open={openModal} onClose={handleModalClose} title={selected?.courseName}>
          <Box
              sx={{
                position: "relative",
                width: "100%",
                paddingTop: "56.25%",
                borderRadius: 2,
                overflow: "hidden",
              }}
          >
            <Box
                component="iframe"
                // src="https://www.youtube.com/embed/1nC2f8ihX0E"
                src={contentSrc}
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  border: 0,
                }}
            />
          </Box>
        </BasicModal>
      </>
  );
}
